import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { businessApi } from "../../lib/ledgerApi";
import { useAuth } from "../../contexts/AuthContext";
import HashString from "../../components/HashString";
import FederationBadge from "../../components/FederationBadge";
import { ListOrdered, ArrowDownLeft, ArrowUpRight, Filter } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const fmtNum = (s) => {
  try {
    const n = Number(s);
    if (Number.isFinite(n)) return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
  } catch {}
  return s;
};

const ALL = "__all__";
const CURRENCIES = ["NEO", "USD", "EUR", "GBP", "INR", "JPY", "AED"];

export default function Transactions() {
  const { business } = useAuth();
  const [txns, setTxns] = useState([]);
  const [currency, setCurrency] = useState(ALL);
  const [direction, setDirection] = useState("all"); // all | sent | received
  const [addresses, setAddresses] = useState(new Set());

  useEffect(() => {
    if (!business) return;
    businessApi
      .wallets()
      .then((ws) => setAddresses(new Set(ws.map((w) => w.ledger_address))))
      .catch(() => {});
  }, [business]);

  useEffect(() => {
    if (!business) return;
    let alive = true;
    const load = async () => {
      const params = { limit: 200 };
      if (currency !== ALL) params.currency = currency;
      try {
        const t = await businessApi.transactions(params);
        if (alive) setTxns(t);
      } catch {
        // super_admin without business context will hit 400 — silently ignore
      }
    };
    load();
    const i = setInterval(load, 4000);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, [currency, business]);

  const filtered = txns.filter((tx) => {
    const out = addresses.has(tx.from_address);
    if (direction === "sent") return out;
    if (direction === "received") return !out;
    return true;
  });

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Transactions</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
          <ListOrdered className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
          History
        </h1>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <Filter className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger data-testid="tx-filter-currency" className="rounded-sm w-44 font-mono">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All currencies</SelectItem>
            {CURRENCIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={direction} onValueChange={setDirection}>
          <SelectTrigger data-testid="tx-filter-direction" className="rounded-sm w-44 font-mono">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All direction</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="received">Received</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs font-mono text-muted-foreground ml-auto">{filtered.length} shown</span>
      </div>

      <div className="border border-border rounded-sm bg-card divide-y divide-border">
        {filtered.length === 0 && <div className="p-4 text-sm text-muted-foreground">No transactions match these filters.</div>}
        {filtered.map((tx) => {
          const out = addresses.has(tx.from_address);
          return (
            <Link key={tx.tx_id} to={`/app/tx/${tx.tx_id}`} data-testid={`tx-row-${tx.tx_id}`} className="grid grid-cols-12 gap-3 p-4 items-center hover:bg-muted/40 transition-colors">
              <div className="col-span-1 hidden sm:flex justify-center">
                {out ? <ArrowUpRight className="w-4 h-4 text-status-tampered" strokeWidth={1.5} /> : <ArrowDownLeft className="w-4 h-4 text-status-ok" strokeWidth={1.5} />}
              </div>
              <div className="col-span-12 sm:col-span-5 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1 flex items-center gap-2 flex-wrap">
                  <span>{tx.kind === "coinbase" ? "Treasury mint" : out ? "Sent" : "Received"} · {tx.status}{tx.block_number != null && ` · #${tx.block_number}`}</span>
                  {tx.bundle_id && (
                    <span data-testid={`tx-bundle-badge-${tx.tx_id}`} className="px-1.5 py-0.5 border border-status-info/40 bg-status-info/10 text-status-info rounded-sm font-mono normal-case tracking-normal">
                      FX bundle · {tx.bundle_role || "leg"}
                    </span>
                  )}
                  {tx.block_number != null && (
                    <FederationBadge blockNumber={tx.block_number} testid={`tx-fed-badge-${tx.tx_id}`} />
                  )}
                </div>
                <HashString value={tx.tx_id} label="Tx id" />
                {tx.memo && <div className="text-[11px] text-muted-foreground mt-1 truncate">{tx.memo}</div>}
              </div>
              <div className="col-span-7 sm:col-span-3 min-w-0 text-xs font-mono text-muted-foreground">
                <div className="truncate">{out ? "to " : "from "} <span className="text-foreground">{(out ? tx.to_address : tx.from_address).slice(0, 18)}…</span></div>
                <div className="text-[10px]">{new Date(tx.timestamp).toLocaleString()}</div>
              </div>
              <div className="col-span-5 sm:col-span-3 text-right font-mono">
                <span className={out ? "text-status-tampered" : "text-status-ok"}>{out ? "−" : "+"}{fmtNum(tx.amount)}</span>{" "}
                <span className="text-muted-foreground">{tx.currency}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
